import { useContext, useEffect, useMemo, useRef } from "react";

import { useToast } from "@hightouch/htui";
import { captureException } from "src/lib/monitoring";
import { debounce, isEqual } from "lodash";
import {
  DeepPartial,
  FieldValues,
  useForm,
  UseFormProps,
} from "react-hook-form";

import {
  encode,
  useEncodedSearchParams,
} from "src/hooks/use-encoded-search-params";

import { HightouchFormContext, UseHightouchFormReturn } from "./form";

const DebounceTime = 300;

export type HightouchSubmitHandler<
  T extends FieldValues,
  C extends Record<string, unknown> = Record<string, unknown>,
  R = void,
> = (data: T, ctx?: C) => Promise<R>;

export type UseHightouchFormProps<
  T extends FieldValues,
  C extends Record<string, unknown>,
  R,
> = {
  success?: string | boolean | ((data: T) => string);
  error?: string;
  errorMessage?: string;
  onSubmit: HightouchSubmitHandler<T, C, R>;
  onError?: (error: any) => any;
  values?: DeepPartial<T>;
  submitOnChange?: boolean;
  /** Hosts that reconcile in-flight edits can own the successful-save baseline. */
  resetAfterSubmit?: boolean;
  /**
   * If provided, the form state will be read (on mount) and written to a URL query string for sharability.
   */
  queryKey?: string;
} & UseFormProps<T>;

export const useHightouchFormContext = () => useContext(HightouchFormContext);

export function useHightouchForm<
  T extends FieldValues,
  C extends Record<string, unknown> = Record<string, unknown>,
  R = any,
>({
  onSubmit,
  onError,
  values,
  success = "Changes saved",
  error = "There was a problem saving your changes",
  errorMessage,
  queryKey,
  submitOnChange,
  resetAfterSubmit = true,
  ...props
}: UseHightouchFormProps<T, C, R>): UseHightouchFormReturn<T, C, R> {
  const [urlState] = useEncodedSearchParams<T>(queryKey);

  const savedValues = useRef(values);
  const isInitialized = useRef(false);

  const { toast } = useToast();

  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  const urlDefaultValues = useMemo(() => {
    if (queryKey) {
      return (urlState as DeepPartial<T> | null) ?? undefined;
    }

    return undefined;
  }, []);

  const form = useForm<T>({
    ...props,
    defaultValues: urlDefaultValues ?? values ?? props.defaultValues,
  });

  const { isDirty } = form.formState;

  const submit = async (_e?: MouseEvent, ctx?: C) => {
    form.clearErrors();
    let result: R | undefined;

    await form.handleSubmit(
      // Callback to handle successful form submission when form is valid
      async (data) => {
        // Promise can be rejected if onError rethrows it
        try {
          result = await onSubmit(data, ctx);
          // Clear isDirty so FormBar hides after save; keepValues so
          // edits made during the in-flight save aren't clobbered.
          if (resetAfterSubmit) form.reset(data, { keepValues: true });
          if (typeof success === "string" || typeof success === "function") {
            toast({
              id: "save",
              title: typeof success === "function" ? success(data) : success,
              variant: "success",
            });
          }
        } catch (e) {
          // When onError is defined, assume the callsite will handle any error is receives.
          // If no onError handler, send to sentry for tracking.
          if (!onError) {
            captureException(e);
          }

          toast({
            id: "save-error",
            title: error,
            message: errorMessage || e?.message,
            variant: "error",
          });
          // Promise can be rejected if onError rethrows it
          try {
            onError?.(e);
          } catch (e1) {
            result = undefined;
            throw e1;
          }
        }

        return result;
      },
      // Callback to handle validation errors
      (errors) => {
        if (typeof error === "string" || typeof error === "function") {
          toast({
            id: "save-error",
            title: error,
            message: errorMessage,
            variant: "error",
          });
        }
        onError?.(errors);
        result = undefined;
      },
    )();

    return result;
  };

  // Subscribe to form changes and mirror them into the URL.
  //
  // We bypass React Router's setSearchParams and call replaceState directly
  // so that typing in a form does not force the entire RouterProvider tree
  // to re-render on every keystroke. Nothing in the app reads back from
  // this URL value after mount — useHightouchForm only reads urlState once
  // (via the `urlDefaultValues` memo + the mount-only reset effect below)
  // so the URL is purely a sharability bookmark from this point on.
  useEffect(() => {
    if (queryKey) {
      const debouncedSetUrlState = debounce((formValues: DeepPartial<T>) => {
        const params = new URLSearchParams(window.location.search);
        params.set(queryKey, encode(formValues));
        const url = new URL(window.location.href);
        url.search = params.toString();
        window.history.replaceState(window.history.state, "", url);
      }, DebounceTime);

      const subscription = form.watch(debouncedSetUrlState);

      return () => {
        subscription.unsubscribe();
        // A pending call would otherwise fire after unmount, writing the URL
        // from a form that is gone.
        debouncedSetUrlState.cancel();
      };
    }

    return;
  }, [queryKey, form.watch]);

  // If no url state is preset, reset the form to the default values
  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  useEffect(() => {
    if (queryKey && !urlState) {
      form.reset(values);
    }
  }, [queryKey, urlState, form.reset]);

  // On mount: set the url form state to be the initial state
  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  useEffect(() => {
    if (
      !isInitialized.current &&
      queryKey &&
      urlState &&
      !isEqual(urlState, savedValues.current)
    ) {
      form.reset(urlState as DeepPartial<T>, {
        keepIsSubmitted: true,
        keepTouched: true,
      });
      isInitialized.current = true;
    }
  }, [queryKey, urlState]);

  // keepDirtyValues prevents an upstream refetch mid-edit from clobbering
  // the user's in-progress changes.
  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  useEffect(() => {
    if (!queryKey && !isEqual(savedValues.current, values)) {
      form.reset(values, {
        keepIsSubmitted: true,
        keepTouched: true,
        keepDirtyValues: true,
      });
      savedValues.current = values;
    }
  }, [queryKey, values]);

  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  useEffect(() => {
    if (submitOnChange && isDirty) {
      submit();
    }
  }, [submitOnChange, isDirty]);

  return { ...form, submit };
}
